/**
 * Tags Service Layer
 *
 * Provides a clean, type-safe interface for tag operations.
 * Used by React Query hooks for data fetching and mutations.
 *
 * Benefits:
 * - Centralized API logic
 * - Easy to test and mock
 * - Type-safe with TypeScript
 * - Consistent error handling
 * - Single source of truth for API endpoints
 */

import { apiClient } from './api.client';

export interface Tag {
  id: string;
  nameEN: string;
  nameFR: string;
  slug: string;
  description?: string;
  color?: string;
  icon?: string;
  category?: string;
  sortOrder: number;
  isActive: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
  };
}

export interface TagCreateInput {
  nameEN: string;
  nameFR: string;
  description?: string;
  color?: string;
  icon?: string;
  category?: string;
  sortOrder?: number;
}

export interface TagUpdateInput extends Partial<TagCreateInput> {
  isActive?: boolean;
}

export interface TagFilters {
  category?: string;
  search?: string;
  active?: boolean;
  lang?: 'en' | 'fr';
}

export interface TagStats {
  id: string;
  nameEN: string;
  nameFR: string;
  category?: string;
  usageCount: number;
}

/**
 * Tags Service
 * All tag-related API operations
 */
export const tagsService = {
  /**
   * Get all tags with optional filters
   */
  async getAll(filters?: TagFilters): Promise<Tag[]> {
    const searchParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
    }
    return apiClient.get('tags', { searchParams }).json<Tag[]>();
  },

  /**
   * Get a single tag by ID
   */
  async getById(id: string): Promise<Tag> {
    return apiClient.get(`tags/${id}`).json<Tag>();
  },

  /**
   * Create a new tag
   */
  async create(data: TagCreateInput): Promise<Tag> {
    return apiClient.post('tags', { json: data }).json<Tag>();
  },

  /**
   * Update an existing tag
   */
  async update(id: string, data: TagUpdateInput): Promise<Tag> {
    return apiClient.put(`tags/${id}`, { json: data }).json<Tag>();
  },

  /**
   * Delete a tag
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`tags/${id}`);
  },

  /**
   * Get tag usage statistics
   */
  async getStats(): Promise<TagStats[]> {
    return apiClient.get('tags/stats').json<TagStats[]>();
  },

  /**
   * Merge multiple tags into one
   */
  async merge(sourceTagIds: string[], targetTagId: string): Promise<void> {
    await apiClient.post('tags/merge', {
      json: { sourceTagIds, targetTagId },
    });
  },

  /**
   * Bulk create tags
   */
  async bulkCreate(tags: TagCreateInput[]): Promise<Tag[]> {
    return apiClient.post('tags/bulk', { json: { tags } }).json<Tag[]>();
  },
};

// Re-export types for convenience
export type { Tag as TagType };
